import {
	Component,
	ElementRef,
	EventEmitter,
	Output,
	ViewChild,
} from '@angular/core';
import {
	MatStepper,
	MatStepperIcon,
	MatStep,
	MatStepLabel,
} from '@angular/material/stepper';
import { Router } from '@angular/router';
import { UserType } from 'src/app/components/user-search-bar/user-search-bar.component';
import { emptyCompany } from 'src/app/models/data/company';
import {
	CreateCompanyEntityRelationshipRequestBody,
	EntityRelationshipDetail,
	RelationshipType,
} from 'src/app/models/data/entityRelationship';
import { emptyUser } from 'src/app/models/data/user';
import { UserProfile } from 'src/app/models/model';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import {
	AboutCompanyEvent,
	AddCompanyAboutComponent,
	CompanyType,
} from './add-company-about/add-company-about.component';
import { CompanyRelationshipEvent } from './add-company-about/add-company-about.component';
import {
	AddCompanyConfirmationComponent,
	AddCompanyConfirmationType,
} from './add-company-confirmation/add-company-confirmation.component';
import Company = Components.Schemas.Company;
import User = Components.Schemas.User;
import { CreateCompanyResponse } from 'src/app/models/response';
import { UserExistsSnackbarComponent } from '../../components/user-exists-snackbar/user-exists-snackbar.component';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { AppNavigationHeaderComponent } from '../../components/navigation-header/app-navigation-header.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

enum StepperPage {
	about = 0,
	confirmation = 1,
}

@Component({
	selector: 'app-add-company',
	templateUrl: './add-company-main-tab.component.html',
	styleUrls: ['./add-company-main-tab.component.css', '../common/stepper.scss'],
	imports: [
		AppNavigationHeaderComponent,
		MatButton,
		MatStepper,
		MatStepperIcon,
		MatIcon,
		MatStep,
		MatStepLabel,
		AddCompanyAboutComponent,
		AddCompanyConfirmationComponent,
	],
})
export class AddCompanyMainTabComponent {
	@Output() addCompanyRequestStatusEvents =
		new EventEmitter<AddCompanyRequestStatusEvent>();
	editCompany: EditCompany = {
		company: emptyCompany(),
		relationshipType: '',
		detail: {},
		adminUser: {
			user: emptyUser(),
			userType: UserType.newUser,
			hasUser: false,
		},
	};
	companyType: CompanyType = CompanyType.newCompany;

	@ViewChild(AddCompanyAboutComponent)
	addCompanyAbout?: AddCompanyAboutComponent;
	@ViewChild(AddCompanyConfirmationComponent)
	addCompanyConfirmation?: AddCompanyConfirmationComponent;
	@ViewChild('stepperRef') stepperRef!: ElementRef;
	snackBarRef?: MatSnackBarRef<UserExistsSnackbarComponent>;

	customCompanyId?: string;
	notes?: string;
	stepper!: MatStepper;

	canContinue = false;
	isEditable = false;

	serverCallInProgress = false;

	newCompanyTitle = $localize`:@@addCompanyMainTab-newCompanyTitle:Add Company`;
	existingCompanyTitle = $localize`:@@addCompanyMainTab-existingCompanyTitle:Add to My Companies List`;

	confirmationType: AddCompanyConfirmationType;

	constructor(
		private readonly router: Router,
		private readonly apiService: ApiService,
		private readonly navHistory: NavigationHistoryService,
		private snackBar: MatSnackBar
	) {
		this.editCompany.company =
			this.router.lastSuccessfulNavigation?.extras.state?.company ??
			emptyCompany();

		this.confirmationType = this.editCompany.company.id
			? AddCompanyConfirmationType.addToMyList
			: AddCompanyConfirmationType.createCompany;

		this.companyType = this.editCompany.company?.id
			? CompanyType.existingCompany
			: CompanyType.newCompany;
		this.editCompany.adminUser.user = emptyUser();
		this.editCompany.adminUser.userType = this.editCompany.company.id
			? UserType.existingUser
			: UserType.newUser;
		this.editCompany.adminUser.hasUser =
			this.router.lastSuccessfulNavigation?.extras.state?.hasUser ?? false;

		this.stepper = this.stepperRef as unknown as MatStepper;
	}

	get addCompanyTitle(): string {
		return this.companyType === CompanyType.newCompany
			? this.newCompanyTitle
			: this.existingCompanyTitle;
	}

	get subtitle(): string {
		return this.editCompany.company.name;
	}

	movePrevious(stepper: MatStepper): void {
		if (stepper.selectedIndex === StepperPage.about) {
			this.navHistory.navigateBack();
		} else {
			stepper.previous();
		}
	}

	isValid(stepper: MatStepper): boolean {
		if (stepper.selectedIndex === StepperPage.about) {
			return this.addCompanyAbout?.isValid() ?? false;
			// } else if (stepper.selectedIndex === StepperPage.confirmation) {
			//   return this.addCompanyConfirmation?.isValid() ?? false;
		}
		return false;
	}

	async moveNext(stepper: MatStepper): Promise<void> {
		if (stepper.selectedIndex === StepperPage.about) {
			if (this.isValid(stepper)) {
				if (!this.editCompany?.adminUser?.user?.id) {
					const isUserExisted = await this.checkForExactEmailMatch();
					if (isUserExisted) {
						this.snackBarRef = this.snackBar.openFromComponent(
							UserExistsSnackbarComponent,
							{
								duration: 3000,
								panelClass: 'existing-user-snackbar',
							}
						);
						return;
					}
				}

				if (!(await this.addCompanyAbout?.doesCompanyAlreadyExist())) {
					this.companyType = CompanyType.newCompany;
				} else {
					this.companyType = CompanyType.existingCompany;
				}
				this.createCompany();
				stepper.next();
			}
		}
	}

	async checkForExactEmailMatch(): Promise<boolean> {
		const normalizedEmail =
			this.editCompany?.adminUser?.user?.email?.trim().toLowerCase() ?? '';

		const queryParameters: QueryParam[] = [
			{ param: 'email', value: normalizedEmail },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
		];

		const users = await this.apiService.getAllUsers(queryParameters);
		const existingUser = users.find(
			(user) => user.email?.trim()?.toLowerCase() === normalizedEmail
		);
		return !!existingUser;
	}

	async createCompany(): Promise<void> {
		// make sure the user can't create the record multiple times from clicking the button multiple times quickly
		if (!this.serverCallInProgress) {
			this.serverCallInProgress = true;

			this.addCompanyRequestStatusEvents.emit({
				status: AddCompanyRequestStatus.loading,
			});
			const userProfile: UserProfile = await this.apiService.getUserProfile();
			const parentEntityId =
				userProfile?.activeRoleDetails?.entityRelationship.parentEntityId;

			if (parentEntityId === undefined) {
				this.addCompanyRequestStatusEvents.emit({
					status: AddCompanyRequestStatus.failure,
				});
				console.error('parentEntityId is undefined');
				return;
			}

			let createCompanyResult;
			try {
				if (this.companyType === CompanyType.newCompany) {
					// we need to copy the information about the api usernames from the string field into the array to send to the server
					this.editCompany.company.apiUsernames = [];
					if (this.editCompany.company.apiEnabled) {
						const apiUserNamesSet = new Set<string>(
							this.editCompany.editApiUsernames?.split('\n')
						);
						this.editCompany.company.apiUsernames = [...apiUserNamesSet];
					}

					const newCompanyRequestBody = {
						company: this.editCompany.company,
						relationshipType:
							this.editCompany.relationshipType?.toLocaleLowerCase() as RelationshipType,
						adminUser: this.editCompany.adminUser.user,
						entityRelationshipDetail: {
							notes: this.editCompany.detail.notes,
							externalId: this.editCompany.detail.externalId,
						},
						countryCode: this.editCompany.company.address?.countryCode!,
					};

					createCompanyResult = await this.apiService.createNewCompany(
						newCompanyRequestBody
					);

					this.editCompany.company.id =
						createCompanyResult.data.addedCompany.id;
				} else {
					const requestBody: CreateCompanyEntityRelationshipRequestBody = {
						childEntityId: this.editCompany.company.id,
						relationshipType:
							this.editCompany.relationshipType?.toLocaleLowerCase() as RelationshipType,
						active: this.editCompany.company.active,
						externalId: this.editCompany.detail.externalId,
						notes: this.editCompany.detail.notes,
					};
					await this.apiService.createCompanyRelationship(
						requestBody,
						parentEntityId
					);
				}

				this.addCompanyRequestStatusEvents.emit({
					status: AddCompanyRequestStatus.success,
					data: {
						companyName: this.editCompany.company?.name,
						apiUsersNotAdded: createCompanyResult?.data?.apiUsersNotAdded,
					},
				});
			} catch (error) {
				console.error('Error adding company: ', error);
				this.addCompanyRequestStatusEvents.emit({
					status: AddCompanyRequestStatus.failure,
					data: {
						companyName: this.editCompany.company.name,
					},
				});
			}

			this.serverCallInProgress = false;
		}
	}
}

export enum AddCompanyRequestStatus {
	loading,
	success,
	failure,
}

export interface AddCompanyRequestStatusEvent {
	status: AddCompanyRequestStatus;
	data?: {
		companyName: string;
		apiUsersNotAdded?: string[];
	};
}

export interface EditCompany {
	company: Company;
	relationshipType: RelationshipType | '';
	detail: EntityRelationshipDetail;
	adminUser: {
		user: User;
		userType: UserType;
		hasUser: boolean;
	};
	editApiUsernames?: string;
}
